import { useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import "./Kopffaltung.css";

/**
 * Die EINFALTUNG der Kopffelder in den Vorgangs-Formularen (Paket „Positionen mit Präsenz",
 * Entscheid Dirk 08.09.2026).
 *
 * **Warum es diesen Baustein gibt.** Im Bearbeiten-Panel von Angebot und Auftrag wird die
 * meiste Arbeit an den POSITIONEN geleistet — und genau die standen bis hierher ganz unten,
 * hinter elf Kopffeldern, Notiz, Einleitungs- und Ausleitungstext. Wer eine Menge ändern
 * wollte, scrollte an einem Formular vorbei, das er gar nicht anfassen wollte. Die
 * Kopffelder werden deshalb zu EINEM aufklappbaren Abschnitt, der beim Bearbeiten
 * zugeklappt beginnt; die Positionen rücken darüber hinauf.
 *
 * **Warum ein Baustein und nicht zweimal derselbe Handgriff.** Angebot und Auftrag tragen
 * dieselbe Faltung, und die Rechnung könnte sie morgen genauso tragen. Zweimal abgeschrieben
 * wären es beim nächsten Fix zwei verschiedene Faltungen — eine mit Auto-Aufklappen bei
 * Feldfehler, eine ohne, und die ohne verstecke dann den Fehler, um dessentwillen es die
 * Mechanik gibt.
 *
 * **Der Zustand gehört der MASKE, nicht dem Baustein.** `useKopffaltung` gibt ihn als
 * Griff heraus, statt ihn im Kopfblock zu verstecken. Zwei Gründe, und beide sind teuer
 * gelernt:
 *
 *  - Der Startzustand hängt vom WEG ab (Bearbeiten zu, Anlegen auf), und zurückgesetzt wird
 *    er dort, wo auch der Entwurf zurückgesetzt wird — beim Neu-Öffnen, beim Wechsel des
 *    bearbeiteten Vorgangs, beim Kunden- oder Bereichswechsel. Kein Zustand darf einen
 *    Kontextwechsel überleben; die Stellen, die das wissen, sind die Masken.
 *  - Das Auto-Aufklappen beim Speichern-Versuch (`zeigeFeld`) stösst die PRÜFUNG an, und die
 *    steht in der Maske. Ein Baustein, der seinen Zustand für sich behielte, bräuchte dafür
 *    einen imperativen Griff von aussen — also denselben Griff, nur umständlicher.
 *
 * **Eingeklappt heisst versteckt, nicht abgeräumt** (`hidden` statt Ausbau aus dem Baum).
 * `aria-controls` zeigt auf den Inhalt; zeigte es auf etwas, das es zeitweise gar nicht
 * gibt, wäre die Beziehung die Hälfte der Zeit gebrochen. Nebenbei bleiben die Felder
 * dieselben Knoten, sodass ein Auf- und Zuklappen weder Fokus noch Eingabe kostet.
 */

export type Kopffaltung = {
  /** Steht der Kopfblock offen? */
  offen: boolean;
  /** Auf- bzw. zuklappen (die Kopfzeile ruft es). */
  umschalten: () => void;
  /**
   * Aufklappen und danach das Feld mit dieser Kennung fokussieren — der Weg für einen
   * Pflichtfeld-Befund beim Speichern-Versuch.
   *
   * Die REIHENFOLGE ist der ganze Sinn: In einem `hidden`-Abschnitt lässt sich nichts
   * fokussieren. Deshalb wird der Wunsch gemerkt und erst in einem Effekt eingelöst, wenn
   * der Abschnitt gezeichnet und offen ist. Ein `focus()` gleich im Klick liefe gegen den
   * Stand VOR dem Aufklappen und ginge lautlos ins Leere — ein unsichtbarer Fehler, also
   * genau das, wogegen die Mechanik gebaut ist.
   */
  zeigeFeld: (feldId: string) => void;
  /** Auf den Startzustand zurück (Neu-Öffnen, Kontextwechsel). */
  zuruecksetzen: () => void;
  /**
   * Nur aufklappen, ohne Fokuswunsch — für Feldfehler des Servers (ME-Etappe 3, Aufgabe 4):
   * Den Fokus setzt dort `me-formular.zumErstenFehler()` nach dem Zeichnen, und der findet
   * das Feld nur, wenn der Block im SELBEN Zug offen ist (in `hidden` lässt sich nichts
   * fokussieren).
   */
  oeffnen: () => void;
};

export function useKopffaltung(startOffen: boolean): Kopffaltung {
  const [offen, setOffen] = useState(startOffen);
  /** Das Feld, das nach dem nächsten Zeichnen den Fokus bekommen soll (null = keines). */
  const [fokusFeld, setFokusFeld] = useState<string | null>(null);

  useEffect(() => {
    if (fokusFeld === null) return;
    // Erst den Wunsch löschen, dann einlösen: Sonst liefe derselbe Fokus bei jedem
    // weiteren Zeichnen erneut und risse den Bearbeiter aus dem Feld, in das er inzwischen
    // gewechselt ist.
    setFokusFeld(null);
    const feld = document.getElementById(fokusFeld);
    // `focus` steht hinter einer Prüfung, wie `scrollIntoView` in der Bereichsfaltung: Ein
    // Aufruf ins Leere liesse jeden Test scheitern, der ein Formular abschickt.
    if (feld !== null && typeof feld.focus === "function") feld.focus();
  }, [fokusFeld]);

  return {
    offen,
    umschalten: () => setOffen((v) => !v),
    zeigeFeld: (feldId: string) => { setOffen(true); setFokusFeld(feldId); },
    zuruecksetzen: () => { setOffen(startOffen); setFokusFeld(null); },
    oeffnen: () => setOffen(true),
  };
}

export type KopfblockProps = {
  faltung: Kopffaltung;
  /**
   * Der Titel des Vorgangs — er steht IMMER in der Kopfzeile, auch eingeklappt. Ein
   * zugeklappter Abschnitt ohne Aussage wäre eine Tür ohne Schild.
   */
  titel: string;
  /** Die Beschriftung des Betrags, wörtlich wie am Feld darin („Nettobetrag", mit
   *  Positionen „Nettobetrag (berechnet aus Positionen)"). */
  betragLabel: string;
  /** Der Betrag als fertiger Text — genau der, der im Feld steht (siehe `kopfBetragText`). */
  betrag: string;
  /** Die Kennung des eingefalteten Inhalts (`aria-controls`). */
  inhaltId: string;
  /**
   * Zahl der Felder mit Fehler im Block (ME-Etappe 3, Aufgabe 4, Review-Fokus 3) — das
   * Gegenstück zum Fehlerzähler am `me-reiter`: Der Kopfblock ist der Abschnitt der Maske,
   * der zugeklappt sein kann, und ein Fehler darin wäre sonst unsichtbar. 0/weggelassen =
   * kein Zähler.
   */
  fehler?: number | undefined;
  children: ReactNode;
};

/**
 * Die Kopfzeile IST der Knopf (Tastatur: ein Ziel, nicht zwei nebeneinander), und sie zeigt
 * ohne Aufklappen das, wonach man den Vorgang wiedererkennt: seinen Titel und seinen
 * Nettobetrag. Der Betrag steht bewusst mit derselben Beschriftung da wie am Feld drinnen —
 * „(berechnet aus Positionen)" ist die Auskunft, warum er sich von selbst ändert, und die
 * gehört an die Stelle, an der man ihn liest.
 */
export function Kopfblock(p: KopfblockProps): ReactElement {
  const offen = p.faltung.offen;
  return (
    <div className="kfal">
      {/* Die Kopfzeile ist eine ÜBERSCHRIFT mit einem Knopf darin, und zwar auf derselben
          Stufe wie „Positionen" (h3): Beide sind Abschnitte derselben Maske, und wer sich
          per Überschriftennavigation durch ein Formular bewegt, soll den eingefalteten
          Block genauso anspringen können wie den Positionsblock. Ein blosser Knopf wäre in
          dieser Reihe gar nicht aufgetaucht — und ausgerechnet der Abschnitt, den man
          erst öffnen muss, fehlte im Inhaltsverzeichnis. */}
      <h3 className="kfal__ueberschrift">
        <button type="button" className="kfal__kopf" aria-expanded={offen}
          aria-controls={p.inhaltId} onClick={p.faltung.umschalten}>
          {/* Das Zeichen ist reine Zutat: Der Zustand steht in aria-expanded, und zweimal
              angesagt wäre er einmal zu viel. */}
          <span className="kfal__marke" aria-hidden="true">{offen ? "▾" : "▸"}</span>
          <span className="kfal__wort">Kopfdaten</span>
          <span className="kfal__titel">
            {p.titel.trim() === "" ? "ohne Titel" : p.titel}
          </span>
          <span className="kfal__betrag">
            {p.betragLabel} <span className="ki-num">{p.betrag}</span>
          </span>
          {p.fehler !== undefined && p.fehler > 0 && (
            <span className="kfal__fehler">
              {p.fehler === 1 ? "1 Fehler" : `${p.fehler} Fehler`}
            </span>
          )}
        </button>
      </h3>
      <div id={p.inhaltId} className="kfal__inhalt" hidden={!offen}>{p.children}</div>
    </div>
  );
}

export type AufklapperProps = {
  faltung: Kopffaltung;
  /** Der Titel — er steht in der Kopfzeile, offen wie zu. */
  titel: string;
  /** Die Kennung des eingefalteten Inhalts (`aria-controls`). */
  inhaltId: string;
  children: ReactNode;
};

/**
 * Der SCHLICHTE Aufklapper — dieselbe Mechanik wie `Kopfblock` (der Zustand gehört der
 * Maske, `useKopffaltung`), aber ohne Titel- und Betragszeile: nur ein Name und der
 * Pfeil. Für LESENDE Kästen ausserhalb des Kopfblocks, deren Inhalt erst beim Öffnen
 * geladen werden soll — z. B. Zahlungsvereinbarung und Geplante Fälligkeiten im
 * geschlossenen Vorgangsdetail (Gesamtreview, I2): Ohne diesen Riegel riefe jede Zeile
 * einer Liste ihren Abruf sofort ab, obwohl kaum je jemand hinsieht.
 *
 * `children` sollen den Inhalt selbst nur zeichnen, wenn `faltung.offen` gilt (kein
 * Abruf auf Verdacht) — das entscheidet der Aufrufer (`{faltung.offen && <Baustein/>}`),
 * nicht dieser Baustein: Er kennt seinen Inhalt nicht und darf ihn nicht bedingen.
 */
export function Aufklapper(p: AufklapperProps): ReactElement {
  const offen = p.faltung.offen;
  return (
    <div className="kfal">
      <h3 className="kfal__ueberschrift">
        <button type="button" className="kfal__kopf" aria-expanded={offen}
          aria-controls={p.inhaltId} onClick={p.faltung.umschalten}>
          <span className="kfal__marke" aria-hidden="true">{offen ? "▾" : "▸"}</span>
          <span className="kfal__titel">{p.titel}</span>
        </button>
      </h3>
      <div id={p.inhaltId} className="kfal__inhalt" hidden={!offen}>{p.children}</div>
    </div>
  );
}
